import { useState } from "react";
import { Truck, Copy, Check } from "lucide-react";
import { useOrderTracking, useSetOrderTracking } from "@/features/orders/api/orderHooks";

interface TrackingCardProps {
  salesOrderName: string;
  isMarketplace: boolean;
}

export function TrackingCard({ salesOrderName, isMarketplace }: TrackingCardProps) {
  const trackingQuery = useOrderTracking(salesOrderName);
  const setTrackingMutation = useSetOrderTracking(salesOrderName);
  const [trackingInput, setTrackingInput] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [copied, setCopied] = useState(false);

  // Don't show for marketplace orders (they get tracking from the marketplace API)
  if (isMarketplace) return null;

  // Don't show until we know there's a DN
  if (!trackingQuery.data?.delivery_note) return null;

  const { tracking_no, transporter_name, delivery_note } = trackingQuery.data;

  const handleSave = () => {
    if (!trackingInput.trim() || !delivery_note) return;
    setTrackingMutation.mutate(
      { deliveryNote: delivery_note, trackingNo: trackingInput.trim() },
      {
        onSuccess: () => {
          setShowForm(false);
          setTrackingInput("");
        },
      },
    );
  };

  const handleCopy = () => {
    if (!tracking_no) return;
    navigator.clipboard.writeText(tracking_no).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="flex items-center gap-2 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
        <Truck className="h-3.5 w-3.5" />
        Shipping
      </div>

      {tracking_no ? (
        <div className="space-y-2">
          <div>
            <span className="text-[11px] text-muted-foreground">Tracking No.</span>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="font-mono text-sm">{tracking_no}</span>
              <button onClick={handleCopy} className="text-muted-foreground hover:text-foreground transition-colors">
                {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
              </button>
            </div>
          </div>
          {transporter_name && (
            <div>
              <span className="text-[11px] text-muted-foreground">Carrier</span>
              <p className="text-sm mt-0.5">{transporter_name}</p>
            </div>
          )}
          <div>
            <span className="text-[11px] text-muted-foreground">Delivery Note</span>
            <p className="font-mono text-xs mt-0.5 text-muted-foreground">{delivery_note}</p>
          </div>
        </div>
      ) : showForm ? (
        <div className="space-y-2">
          <div className="relative">
            <Truck className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Tracking number"
              value={trackingInput}
              onChange={(e) => setTrackingInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSave()}
              className="w-full pl-8 pr-3 py-1.5 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-ring"
              autoFocus
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={!trackingInput.trim() || setTrackingMutation.isPending}
              className="flex-1 text-xs font-medium py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {setTrackingMutation.isPending ? "Saving..." : "Save"}
            </button>
            <button
              onClick={() => { setShowForm(false); setTrackingInput(""); }}
              className="text-xs font-medium py-1.5 px-3 rounded-lg border hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowForm(true)}
          className="w-full flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-lg border border-dashed hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
        >
          <Truck className="h-3.5 w-3.5" />
          Add Tracking No.
        </button>
      )}
    </div>
  );
}
